import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { cn } from '@mellify/ui';
import { NotificationDropdown } from './NotificationDropdown';
import { useUnreadNotificationCount } from '../notifications/hooks/useNotifications';

export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: countData } = useUnreadNotificationCount();
  const unreadCount = countData?.unreadCount ?? 0;

  // Listen for bell ping events from the notification stream
  useEffect(() => {
    const handleBellPing = () => {
      setIsAnimating(true);
      // Remove animation class after animation completes
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
      return () => clearTimeout(timer);
    };

    window.addEventListener('mellify:notification-bell-ping', handleBellPing);

    return () => {
      window.removeEventListener('mellify:notification-bell-ping', handleBellPing);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative inline-flex items-center justify-center p-2 rounded-lg transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
          isOpen
            ? 'bg-dark-100 dark:bg-dark-700 text-dark-700 dark:text-dark-200'
            : 'text-dark-500 dark:text-dark-400 hover:bg-dark-100 dark:hover:bg-dark-700 hover:text-dark-700 dark:hover:text-dark-200'
        )}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Bell
          className={cn(
            'w-5 h-5 transition-transform',
            isAnimating && 'animate-bell-ring'
          )}
        />

        {/* Unread count badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center">
            <span className="absolute inline-flex h-full w-full rounded-full bg-danger-400 opacity-75 animate-ping" />
            <span
              className={cn(
                'relative inline-flex items-center justify-center rounded-full bg-danger-500 text-white font-bold leading-none',
                unreadCount > 9
                  ? 'min-w-[20px] h-[18px] px-1 text-[10px]'
                  : 'w-[18px] h-[18px] text-[10px]'
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <NotificationDropdown onClose={() => setIsOpen(false)} />
      )}

      {/* Bell ring animation keyframes (injected via style tag) */}
      <style>{`
        @keyframes bell-ring {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-12deg); }
          30% { transform: rotate(10deg); }
          40% { transform: rotate(-8deg); }
          50% { transform: rotate(6deg); }
          60% { transform: rotate(-4deg); }
          70% { transform: rotate(2deg); }
          80% { transform: rotate(-1deg); }
          90% { transform: rotate(1deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-bell-ring {
          animation: bell-ring 0.8s ease-in-out;
          transform-origin: top center;
        }
      `}</style>
    </div>
  );
};

NotificationBell.displayName = 'NotificationBell';